import React, { useCallback, useEffect, useMemo } from 'react'
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import docIcon from "../../../../../assets/icons/mono/Icon-doc.svg"
import playIcon from "../../../../../assets/images/play-icon.png"
import { AppDispatch, RootState } from 'store';
import { GigStepPropertyWithValue, MilestoneFile, MilestoneStep } from 'types/campaign/collaborations';
import { sanitizeURLs } from 'utils/helpers';
import { fetchAddressThunk } from 'slices/addressBook/thunk';
import moment from 'moment';
import InstagramCaption from 'Components/Common/InstagramCaption';

import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation } from "swiper/modules"
import 'swiper/css';
import 'swiper/css/navigation';
import { fetchBasicPostThunk, fetchBasicSocialPostThunk } from 'slices/influencer/thunk';
import { Post } from 'types/influencer';
import { showInfluencerPostModal } from 'slices/ui/reducer';
import { useParams } from 'react-router-dom';

interface PropertiesPreviewProps {
    step: MilestoneStep,
    files: MilestoneFile[],
    setSelectedMedia: (media: any) => void,
    deliverables: any,
    activeView?: boolean
}

const PropertiesPreview: React.FC<PropertiesPreviewProps> = ({ step, files, setSelectedMedia, deliverables, activeView=false }) => {

    const dispatch: AppDispatch = useDispatch();

    const { campaignId, influencerId } = useParams();
    const collaborationData = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.collaborations?.[influencerId!]);
    const collaboration = collaborationData?.collaboration ?? null;
    const basicPosts = useSelector((state: RootState) => state.influencers.basic_social_posts);

    const addressBook = useSelector((state: RootState) => state.addressBook.items);

    const properties = useMemo(() => {
        const keys = Object.keys((deliverables ?? {}));
        const _properties: GigStepPropertyWithValue[] = [];
        for (let key of keys) {
            const property = step.gig_step.properties.find((pr) => (pr.key === key || pr.key === 'post' && key === 'social_post'));
            if (property && !_properties.map((p) => p.key).includes(property.key)) _properties.push({ ...property, value: (deliverables ?? {})[key] });
        }

        return _properties.sort((a, b) => a.sort_order - b.sort_order);
    }, [step, deliverables])

    useEffect(() => {
        const addressProperties = properties.filter((property) => property.type === 'address');
        for (let addressProperty of addressProperties) {
            if (addressProperty.value && typeof addressProperty.value === 'string')
                dispatch(fetchAddressThunk({ addressUUID: addressProperty.value }))
        }
    }, [properties])

    useEffect(() => {
        if (['post_selection', 'reel_selection', 'story_selection', 'video_selection'].includes(step.gig_step.slug)) {
            if (Array.isArray(deliverables.social_post)) {
                for (let postId of deliverables.social_post) {
                    dispatch(fetchBasicSocialPostThunk({postId,force: true}));
                }
            }
        }
    }, [step, deliverables])

    const getCombinedAddress = (address: any | undefined | null) => {

        if (address) {
            const combinedAddress = `${address.premise ? address.premise+',' : ''} ${address.sublocality ? address.sublocality+',' : ''} ${address.city ? address.city+',' : ''} ${address.region ? address.region+',' : ''} ${address.pincode}`;
            return combinedAddress;
        }

        return "*";
    }

    const getFilePreview = useCallback((fileId: string) => {
        const file = files.find((file) => file.step === step.gig_step.slug && file.uuid === fileId);
        return file
    }, [files, step]) 

    const viewPost = (post: any) => {
        dispatch(showInfluencerPostModal({ postId: post.uuid, influencer: collaboration.influencer, socialAccount: collaboration.social_account, status: post.status as "pending" | "approved" | "rejected" | "processing", milestoneStep: step.gig_step.slug }))
    }

    return (
        <div className={activeView ? 'active-step-content' : ''}>
            {
                properties.map((property, i) => {
                    if (property.key === 'approval') return null;

                    if (property.type === 'address') {
                        return (
                            <div key={i} className='property-preview'>
                                <div className="content-wrapper">
                                    {getCombinedAddress(addressBook[property.value as string]?.address)}
                                </div>
                            </div>
                        )
                    }

                    if (property.type === 'post') {
                        return (
                            <div key={i} className='property-preview'>
                                <div className="content-wrapper">
                                    {
                                        deliverables?.social_post?.length > 0 &&
                                        <Swiper
                                            slidesPerView={'auto'}
                                            spaceBetween={18}
                                            navigation={true}
                                            modules={[Navigation]}
                                            freeMode={true}
                                            className="mySwiper">
                                            {
                                                deliverables.social_post.map((postId: string, i: number) => {
                                                    const post = basicPosts[postId]?.post;
                                                    if(!post) return null;

                                                    return (
                                                        <SwiperSlide className="milestone-view-ig-media-preview w-auto" key={i}>
                                                            <div onClick={() => viewPost(post)} className="position-relative post-preview-thumbnail bgc-content bg-media" style={{ backgroundImage: `url(${post?.thumbnail_url})` }}>
                                                            </div>
                                                        </SwiperSlide>

                                                    )
                                                })
                                            }
                                        </Swiper>
                                    }
                                </div>
                            </div>
                        )
                    }

                    if (['file', 'large_file'].includes(property.type)&& property.value && typeof property.value === 'string') {
                        return (
                            <div key={i} className='property-preview'>
                                {
                                    getFilePreview(property.value) &&
                                    <>
                                        {
                                            getFilePreview(property.value)?.type === 'image' &&
                                            <div onClick={() => setSelectedMedia(getFilePreview(property.value as string))} className={`cursor-pointer bg-media bgc-black ${activeView ? 'raw-media-thumbnail' : 'gig-image'}`} style={{backgroundImage: `url(${getFilePreview(property.value)?.url})`}}></div>
                                        }
                                        {
                                            getFilePreview(property.value)?.type === 'video' &&
                                            <div className={`position-relative ${activeView ? 'raw-media-thumbnail' : 'gig-image'}`}>
                                                <video onClick={() => setSelectedMedia(getFilePreview(property.value as string))} className={`cursor-pointer bgc-black ${activeView ? 'raw-media-thumbnail' : 'gig-image'} rounded-2`} src={getFilePreview(property.value)?.url}></video>
                                                {!activeView && <img src={playIcon} className='video-play-icon' />}
                                            </div>
                                       }
                                        {
                                            getFilePreview(property.value)?.type === 'other' &&
                                            <a href={getFilePreview(property.value)?.url} target='_blank' className='d-block gig-image bg-media' style={{backgroundImage: `url(${docIcon})`}}></a>
                                        }
                                    </>
                                }
                            </div>
                        )
                    }

                    if(property.type === 'datetime'){
                        return (
                            <div key={i} className='property-preview'>
                                {
                                properties.length > 1 &&
                                    <label htmlFor="property-preview-value" className="property-preview-label">{property.label}</label>
                                }
                                <div className="content-wrapper">
                                    {moment(property.value).format('MMMM Do YYYY, h:mm A')}
                                </div>
                            </div>
                        )
                    }

                    if(property.type === 'caption'){
                        return (
                            <div key={i} className='property-preview'>
                                {
                                    properties.length > 1 &&
                                    <label htmlFor="property-preview-value" className="property-preview-label">{property.label}</label>
                                }
                                <InstagramCaption fontSize={'12px'} text={String(property.value)} />
                            </div>
                        )
                    }

                    return (
                        <div key={i} className='property-preview'>
                            {
                                properties.length > 1 &&
                                <label htmlFor="property-preview-value" className="property-preview-label">{property.label}</label>
                            }
                            {
                                <div
                                    className={`content-wrapper`}
                                >
                                    <div
                                       dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                sanitizeURLs(
                                                    typeof property.value === 'string'
                                                        ? property.value
                                                        : typeof property.value === 'object' && property.value !== null && 'text' in property.value
                                                            ? (property.value as any).text ?? ''
                                                            : ''
                                                )
                                            ),
                                        }}
                                    />
                                </div>
                            }
                        </div>
                    )
                })
            }
        </div>
    )
}

export default PropertiesPreview